import { useMemo, useRef } from 'react';
import { callDeepLink, compareWithAppVersion, toQueryString } from '@croquiscom/web2app';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';

import { useUserAgent, useZigzag } from '@common/device-manager';
import { LatestAppVersionPopup, usePopupDialog } from '@common/overlay-manager';
import { getReviewDetailQueryParamsForApp, getReviewDetailQueryString } from '@common/review';
import { useCatalogProductIdParams, usePdpBaseInfo } from '@domains/pdp-common';
import { ReviewListConfigProvider } from '@domains/review-list-common';
import { AdContainer } from '@domains/review-list-feature';

const PdpReviewTabMfeContent = dynamic(() => import('zigzag-review/PdpReviewTabContent'), {
  ssr: true,
});

const PdpReviewTabContentWithProvider = dynamic(
  async () => {
    const mod = await import('zigzag-review/withOptionWrappedApolloClientProvider');
    return {
      default: mod.default(PdpReviewTabMfeContent),
    };
  },
  {
    ssr: true,
  },
);

const preloadMfeReviewChunkFiles = () => {
  setTimeout(async () => {
    try {
      await Promise.all([
        import('zigzag-review/PdpReviewTabContent'),
        import('zigzag-review/withOptionWrappedApolloClientProvider'),
      ]);
    } catch (error) {
      console.error(error);
    }
  }, 300);
};

// NOTE: preload should not run during `next build`/SSR module evaluation.
// It is intended for the browser to warm up remote chunks.
if (typeof window !== 'undefined') {
  preloadMfeReviewChunkFiles();
}

export interface PdpReviewTabContentProps {
  productId: string;
  preview?: boolean;
}

export const PdpReviewTabContent = ({ productId, preview }: PdpReviewTabContentProps) => {
  const isZigzagApp = useZigzag();
  const userAgent = useUserAgent();

  const params = useCatalogProductIdParams();
  const { shop } = usePdpBaseInfo(params);

  const shopMainDomainRef = useRef<string>(shop.main_domain);
  shopMainDomainRef.current = shop.main_domain;

  const { open } = usePopupDialog();

  const openLinkInApp = ({ url, query, app_version }: { url: string; query?: object; app_version?: string }) => {
    if (app_version && !compareWithAppVersion(app_version)) {
      open(LatestAppVersionPopup());
      return;
    }

    callDeepLink(url)(query);
  };

  const path = useMemo(() => {
    return {
      photo_review: {
        detail: ({
          catalog_product_id,
          review_id,
          attachment_position,
        }: {
          catalog_product_id: string;
          review_id: string;
          attachment_position: number;
        }) => {
          if (isZigzagApp) {
            openLinkInApp({
              url: `/photo_review?${toQueryString({
                product_id: catalog_product_id,
                review_id,
                attachment_position,
              })}`,
              app_version: '7.23.0',
            });
          }
          return '';
        },
        list: ({
          catalog_product_id,
          review_id,
          attachment_position,
        }: {
          catalog_product_id: string;
          review_id: string;
          attachment_position: number;
        }) => {
          if (isZigzagApp) {
            openLinkInApp({
              url: `/photo_review?${toQueryString({
                product_id: catalog_product_id,
                review_id,
                attachment_position,
              })}`,
              app_version: '7.23.0',
            });
          }
          return '';
        },
        gallery: (catalogProductId: string) => {
          if (isZigzagApp) {
            openLinkInApp({
              url: `/photo_review_gallery?${toQueryString({ product_id: catalogProductId })}`,
              app_version: '7.23.0',
            });
            return '';
          }

          return `/review/gallery/${catalogProductId}`;
        },
      },
      review: {
        detail: (reviewId, isReplyScroll) => {
          if (isZigzagApp) {
            openLinkInApp({
              url: `/review_detail?${toQueryString(
                getReviewDetailQueryParamsForApp({
                  reviewId,
                  catalogProductId: productId,
                  isReplyScroll,
                }),
              )}`,
              app_version: '7.85.0',
            });
            return '';
          }
          const queryString = getReviewDetailQueryString({
            catalogProductId: productId,
            isReplyScroll,
            fromProductPreview: false,
          });
          return `/review/detail/${reviewId}${queryString ? `?${queryString}` : ''}`;
        },
        report: (reviewId) => `/review-report/${reviewId}`,
        list: (productId) => {
          if (isZigzagApp) {
            openLinkInApp({
              url: `/browser_product_review?${toQueryString({
                shop_main_domain: shop.main_domain,
                product_id: productId,
              })}`,
            });
            return '';
          }

          return `/review/list/${productId}`;
        },
        reviewer_review_list: (reviewer_id: string) => {
          if (isZigzagApp) {
            openLinkInApp({ url: `/review_profile?${toQueryString({ reviewer_id })}`, app_version: '7.81.0' });
            return '';
          }

          return `/reviewer/${reviewer_id}/list`;
        },
      },
    } as const;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZigzagApp]);

  return (
    <>
      {/* ! NOTE: 광고 컴포넌트(AdContainer)를 위해 기존 레거시 리뷰 리스트 프로바이더 추가 */}
      <ReviewListConfigProvider
        value={{
          isZigzagApp: isZigzagApp ?? false,
          catalog_product_id: productId,
          is_product_preview: false,
          path,
        }}
      >
        <PdpReviewTabContentWrapper>
          <PdpReviewTabContentWithProvider
            userAgent={userAgent}
            productId={productId}
            path={path}
            renderAdComponent={() => {
              if (preview) {
                return null;
              }

              return <AdContainer is_empty_review={false} />;
            }}
          />
        </PdpReviewTabContentWrapper>
      </ReviewListConfigProvider>
    </>
  );
};

const PdpReviewTabContentWrapper = styled.div`
  padding: 0 16px 100px;
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
`;
